"use client";

import { useState, useCallback, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { Typography } from "../ui/typography";
import { cn } from "@/lib/utils";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  EnergyIcon,
  Location05FreeIcons,
  StarIcon,
} from "@hugeicons/core-free-icons";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { Accommodation, getAccommodationHref } from "@/types/accommodations";
import { useTopLoader } from "nextjs-toploader";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselDots,
  CarouselNext,
  CarouselPrevious,
  useCarousel,
} from "@/components/ui/carousel";
import { Skeleton } from "../ui/skeleton";
import { ACCOMMODATION_PLACEHOLDER_IMAGE } from "@/constants/images";
import { getAccommodationImageSrc } from "@/utils/getAccommodationImageSrc";

interface AccommodationCardProps {
  accommodation: Accommodation;
  checkIn?: string;
  checkOut?: string;
}

// Image carousel component for desktop
function ImageCarousel({
  images,
  title,
  showNavigation,
}: {
  images: Accommodation["images"];
  title: string;
  showNavigation: boolean;
}) {
  const { canScrollPrev, canScrollNext, scrollPrev, scrollNext } =
    useCarousel();

  return (
    <>
      <CarouselContent className="h-full">
        {images?.slice(0, 5).map((img, idx) => (
          <CarouselItem key={idx} className="h-full pl-0">
            <div className="relative w-full h-full aspect-square">
              <ImageWithSkeleton
                src={getAccommodationImageSrc(img?.fileKey)}
                alt={title}
                priority={idx === 0}
              />
            </div>
          </CarouselItem>
        ))}
      </CarouselContent>

      {/* Navigation buttons - visible on hover */}
      {showNavigation && (
        <>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              scrollPrev();
            }}
            disabled={!canScrollPrev}
            className={cn(
              "absolute top-1/2 right-[10px] -translate-y-1/2 z-10",
              "size-7 rounded-full bg-white flex items-center justify-center",
              "transition-opacity disabled:opacity-0",
              "cursor-pointer",
            )}
            aria-label="Previous image"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              scrollNext();
            }}
            disabled={!canScrollNext}
            className={cn(
              "absolute top-1/2 left-[10px] -translate-y-1/2 z-10",
              "size-7 rounded-full bg-white flex items-center justify-center",
              "transition-opacity disabled:opacity-0",
              "cursor-pointer",
            )}
            aria-label="Next image"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
        </>
      )}

      {/* Dots pagination */}
      <CarouselDots
        className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10"
        activeClassName="!bg-white-500"
        inactiveClassName="bg-white-500/70"
      />
    </>
  );
}

// Image with skeleton loading
function ImageWithSkeleton({
  src,
  alt,
  priority = false,
}: {
  src: string;
  alt: string;
  priority?: boolean;
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [imgSrc, setImgSrc] = useState(src);

  useEffect(() => {
    setImgSrc(src);
    setIsLoading(true);
  }, [src]);

  return (
    <>
      {isLoading && <Skeleton className="absolute inset-0 w-full h-full" />}
      <Image
        src={imgSrc}
        alt={alt}
        fill
        className="object-cover"
        loading={priority ? "eager" : "lazy"}
        onLoad={() => setIsLoading(false)}
        onError={() => {
          if (imgSrc !== ACCOMMODATION_PLACEHOLDER_IMAGE) {
            setImgSrc(ACCOMMODATION_PLACEHOLDER_IMAGE);
          }
          setIsLoading(false);
        }}
        unoptimized
      />
    </>
  );
}

// Skeleton for loading state
export function AccommodationCardSkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="aspect-[8/5] md:aspect-[135/100] rounded-lg" />
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-12" />
        </div>
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-5 w-24" />
        </div>
      </div>
    </div>
  );
}

const AccommodationCard = ({
  accommodation,
  checkIn,
  checkOut,
}: AccommodationCardProps) => {
  const [hovered, setHovered] = useState(false);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const hasMultipleImages = accommodation?.images?.length > 1;
  const topLoader = useTopLoader();

  const handleCarouselAreaClick = useCallback(
    (e: React.MouseEvent) => {
      // Only prevent navigation if clicking on carousel controls
      const target = e.target as HTMLElement;
      const isCarouselControl =
        target.closest("button") ||
        target.closest("[data-slot='carousel-dots']");

      if (isCarouselControl) {
        e.preventDefault();
        e.stopPropagation();

        requestAnimationFrame(() => {
          if (topLoader) {
            topLoader.done();
          }
        });
      }
    },
    [topLoader],
  );

  const CardContent = () => (
    <div
      className="space-y-3 group"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="relative aspect-[8/5] md:aspect-[135/100] rounded-lg overflow-hidden">
        {hasMultipleImages && !isMobile ? (
          <div onClick={handleCarouselAreaClick} className="w-full h-full">
            <Carousel
              opts={{
                loop: false,
                align: "start",
              }}
              dir="rtl"
              className="w-full h-full"
            >
              <ImageCarousel
                images={accommodation.images}
                title={accommodation.title}
                showNavigation={hovered}
              />
            </Carousel>
          </div>
        ) : (
          <ImageWithSkeleton
            src={getAccommodationImageSrc(accommodation?.images?.[0]?.fileKey)}
            alt={accommodation.title}
            priority
          />
        )}
        <div className="flex items-center gap-2 absolute top-2 right-2">
          {accommodation?.discount ? (
            <div className="bg-error-500 text-white px-3 py-1 md:py-0.5 rounded-full z-10">
              <Typography
                variant="captionMedium"
                className="text-[10px] md:text-[13px]"
              >
                {accommodation?.discountType === "percentage" ? "%" : "تومان"}
                {accommodation?.discount} تخفیف
              </Typography>
            </div>
          ) : null}
          {accommodation?.isNew ? (
            <div className="bg-[#F99B3D] text-white px-3 py-1 md:py-0.5 rounded-full z-10 flex items-center justify-center gap-1">
              <HugeiconsIcon
                icon={EnergyIcon}
                size={16}
                strokeWidth={1.5}
                className="size-4"
              />
              <Typography
                variant="captionMedium"
                className="text-[10px] md:text-[13px]"
              >
                آنی!
              </Typography>
            </div>
          ) : null}
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-3 divide-x-2 divide-black-50">
          <Typography
            variant="captionRegular"
            color="black300"
            className="flex items-center gap-1 pl-3"
          >
            <HugeiconsIcon
              icon={Location05FreeIcons}
              color="var(--text-foreground)"
              size={16}
            />
            {accommodation.provinceName}، {accommodation.cityName}
          </Typography>

          <Typography
            variant="captionRegular"
            className="flex items-center gap-1"
          >
            <HugeiconsIcon
              icon={StarIcon}
              fill="var(--text-foreground)"
              color="var(--text-foreground)"
              size={12}
              className="mb-0.5"
            />
            {accommodation.overallRating}
          </Typography>
        </div>
        <Typography
          variant="bodySMedium"
          className="line-clamp-1 md:font-normal"
        >
          {accommodation.title}
        </Typography>
        <Typography color="black300" variant="captionRegular">
          {accommodation.rooms} اتاق، تا {accommodation.totalCapacity} نفر
        </Typography>
        <div className="flex items-center justify-between">
          <Typography color="black300" variant="captionRegular">
            شروع قیمت از
          </Typography>
          <div className="">
            {accommodation.discount > 0 ? (
              <Typography
                variant="captionMedium"
                className="text-left line-through -mt-4"
                color="black200"
              >
                {accommodation.price.toLocaleString()}
              </Typography>
            ) : null}
            <Typography variant="bodyMRegular" className="text-left">
              {accommodation.discountedPrice.toLocaleString()}
              <Typography variant="captionRegular" as="span" className="mr-1">
                تومان
              </Typography>
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );

  // Discovery results don't always carry a referenceParam; render a plain card
  // rather than linking to /accommodations/undefined.
  const href = getAccommodationHref(accommodation, { checkIn, checkOut });

  if (!href) {
    return <CardContent />;
  }

  return (
    <Link href={href} className="block cursor-pointer">
      <CardContent />
    </Link>
  );
};

export default AccommodationCard;
